import { useState } from 'react';
import { Box, hasBankImage, Icon, Label, Select, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import Dialog from '#shared/components/Dialog/Dialog';
import { DialogComponentProps } from '#shared/providers/Dialog/DialogContext';
import { Button } from '#shared/components/UI';
import { getMaskedAccountLabel } from './accountLabels';
import { useEnvironmentUrls } from '#shared/providers/EnvironmentUrls';
import { BankAccountRow } from './types';
import styles from './AddBillingDialog.module.scss';

const cn = withStyles(styles);

/** The two ways out of the dialog: pick an account that is already connected, or connect a new one. */
export type AddBillingSelection = { source: 'existing'; token: string } | { source: 'connect' };

export type AddBillingDialogProps = DialogComponentProps<AddBillingSelection> & {
  /**
   * Connected bank accounts that aren't billing accounts yet; with none, the dialog offers only the
   * eLinx route. Bank accounts only — the caller filters cards out, which is why there is no card
   * branch below.
   */
  selectableAccounts: BankAccountRow[];
};

// The one billing dialogue that returns a value rather than a decision, so it is the one opened through
// useDialog() instead of useAlertDialog(). It owns the selection: the dialog content unmounts
// when it closes, so a reopened dialog starts back at the placeholder without anyone resetting it.
export default function AddBillingDialog({ selectableAccounts, onConfirm }: AddBillingDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.BillingAccountDialogue.AccountAddBilling' });
  const [selectedToken, setSelectedToken] = useState('');

  return (
    <>
      <Dialog.Title className={cn('title')}>
        {t('addBillingAccount', { defaultValue: 'Add Billing Account' })}
      </Dialog.Title>

      <Box flx="col" width="100%" mt="md">
        {selectableAccounts.length > 0 && (
          <>
            <Label isBold={false} label={t('selectAccount', { defaultValue: 'Select an account' })} required={false} />

            <Select
              version="2"
              options={selectableAccounts}
              optionValue={(account) => account.Token}
              optionLabel={getMaskedAccountLabel}
              name="account"
              value={selectedToken}
              filterable
              placeholder={t('selectPaymentMethod', { defaultValue: 'Select payment method' })}
              sort={false}
              onChange={(e) => {
                setSelectedToken(e.target.value);
              }}
              renderOption={(account, { Option }) => (
                <Option>
                  <AccountOption account={account} />
                </Option>
              )}
              renderSelectedValue={(account, { Placeholder }) =>
                account ? <AccountOption account={account} /> : <Placeholder />
              }
            />

            <Box mt="md" jc="center">
              <Button
                id="addBilling-next"
                colorScheme="secondary"
                size="large"
                onClick={() => {
                  // Nothing picked yet: the legacy Next was likewise a no-op rather than an error.
                  if (selectedToken === '') return;

                  onConfirm({ source: 'existing', token: selectedToken });
                }}
              >
                {t('next', { defaultValue: 'Next' })}
              </Button>
            </Box>

            <div className={cn('lineContainer')}>
              <span>{t('or', { defaultValue: 'OR' })}</span>
            </div>
          </>
        )}

        <Box mt="md" gap="sm" base="8" jc="center">
          <Button
            id="addBilling-addPaymentMethod"
            colorScheme="primary"
            size="large"
            onClick={() => onConfirm({ source: 'connect' })}
          >
            {t('addPaymentMethod', { defaultValue: 'Add Payment Method' })}
          </Button>
        </Box>
      </Box>
    </>
  );
}

function AccountOption({ account }: { account: BankAccountRow }) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.BillingAccountDialogue.AccountAddBilling' });
  const { embedAssetsUrl } = useEnvironmentUrls();

  return (
    <Box flx="row" ai="center">
      {/* Not every BankId has an asset on the host, and an unguarded <img> would show the browser's
          broken-image glyph inside the Select. Same guard as PaymentMethodCard's logo. */}
      {account.BankId && hasBankImage(account.BankId) ? (
        <img
          alt={t('bankLogoAlt', { defaultValue: 'bank logo' })}
          className={cn('logos')}
          src={`${embedAssetsUrl}/images/logos-banks/${account.BankId}.svg`}
        />
      ) : (
        <Icon icon="bankDark" size="sm" />
      )}

      {getMaskedAccountLabel(account)}
    </Box>
  );
}
